import React, { useState } from 'react';
import './Avatar.css';

interface AvatarProps {
  src?: string;
  name: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  showOnlineIndicator?: boolean;
  isOnline?: boolean;
}

const Avatar: React.FC<AvatarProps> = ({
  src,
  name,
  size = 'md',
  className = '',
  showOnlineIndicator = false,
  isOnline = false,
}) => {
  const [imageError, setImageError] = useState(false);

  // Generate initials from name
  const getInitials = (name: string): string => {
    const words = name.trim().split(' ');
    if (words.length >= 2) {
      return `${words[0][0]}${words[1][0]}`.toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  // Generate deterministic color from name
  const getBackgroundColor = (name: string): string => {
    const colors = [
      '#2196F3', // Blue
      '#10b981', // Green
      '#f59e0b', // Amber
      '#ef4444', // Red
      '#8b5cf6', // Purple
      '#ec4899', // Pink
      '#06b6d4', // Cyan
      '#f97316', // Orange
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  const initials = getInitials(name);
  const bgColor = getBackgroundColor(name);
  const showFallback = !src || imageError;

  return (
    <div className={`avatar avatar-${size} ${className}`}>
      {showFallback ? (
        <div
          className="avatar-fallback"
          style={{ backgroundColor: bgColor }}
        >
          <span className="avatar-initials">{initials}</span>
        </div>
      ) : (
        <img
          src={src}
          alt={name}
          className="avatar-image"
          onError={() => setImageError(true)}
        />
      )}
      {showOnlineIndicator && (
        <span className={`avatar-online-indicator ${isOnline ? 'online' : 'offline'}`} />
      )}
    </div>
  );
};

export default Avatar;
